// Localização: frontend/src/components/LandingPage/Footer.jsx
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
    Mail, 
    Phone, 
    MapPin, 
    Facebook, 
    Instagram, 
    Linkedin,
    Twitter,
    Heart,
    Shield,
    Lock
} from 'lucide-react';

const LOGO_PATH = '/images/higeia-icon-only.png';

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const navigate = useNavigate();
    const location = useLocation();

    // Links que devem fazer scroll para o topo ao clicar
    const linksComScroll = ['/sobre-nos', '/privacidade', '/termos', '/contato', '/pricing', '/recursos', '/depoimentos', '/ajuda', '/planos/registro-de-ponto'];

    // Função para lidar com o clique nos links
    const handleLinkClick = (href, e) => {
        // Se o link está na lista de links com scroll, faz scroll para o topo
        if (linksComScroll.includes(href)) {
            e.preventDefault();
            
            // Se já estamos na mesma página, apenas faz scroll
            if (location.pathname === href) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            
            // Se estamos em outra página, faz scroll e depois navega
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Aguarda o scroll completar antes de navegar
            setTimeout(() => {
                navigate(href);
                // Garante que a nova página também comece no topo
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'auto' });
                }, 50);
            }, 300);
        }
    };

    // Links de navegação
    const navigationLinks = [
        { name: 'Recursos', href: '/recursos' },
        { name: 'Planos', href: '/pricing' },
        { name: 'Registro de Ponto', href: '/planos/registro-de-ponto' },
        { name: 'Sobre Nós', href: '/sobre-nos' },
        { name: 'Contato', href: '/contato' },
    ];

    // Links úteis
    const usefulLinks = [
        { name: 'Política de Privacidade', href: '/privacidade' },
        { name: 'Termos de Uso', href: '/termos' },
        { name: 'Depoimentos', href: '/depoimentos' },
        { name: 'Central de Ajuda', href: '/ajuda' },
        { name: 'Blog', href: '/blog' },
    ];

    // Redes sociais
    const socialLinks = [
        { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/higeia.tech' },
        { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/higeia.tech' },
        { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/higeia.tech' },
        { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/higeia.tech' },
    ];

    return (
        <footer className="bg-gray-900 text-gray-300">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                
                {/* Grid Principal do Footer */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                    
                    {/* 1. Coluna: Logo e Descrição */}
                    <div className="lg:col-span-1">
                        <Link to="/" className="flex items-center mb-4">
                            <img 
                                className="h-10 w-auto" 
                                src={LOGO_PATH} 
                                alt="Higeia.tech Logo"
                            />
                            <span className="ml-3 text-xl font-bold text-white">Higeia.tech</span>
                        </Link>
                        <p className="text-sm text-gray-400 leading-relaxed mb-4">
                            Sistema de gestão inteligente para home care e lares de idosos. 
                            Otimizando seu tempo e elevando a qualidade do cuidado.
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Shield className="h-4 w-4 text-higeia-light" />
                            <span>LGPD Compliant</span>
                        </div>
                    </div>

                    {/* 2. Coluna: Navegação */}
                    <div>
                        <h3 className="text-white font-semibold text-lg mb-4">Navegação</h3>
                        <ul className="space-y-3">
                            {navigationLinks.map((link) => (
                                <li key={link.name}>
                                    {link.href.startsWith('/') ? (
                                        <Link 
                                            to={link.href}
                                            onClick={(e) => handleLinkClick(link.href, e)}
                                            className="text-gray-400 hover:text-higeia-light transition-colors duration-200 text-sm"
                                        >
                                            {link.name}
                                        </Link>
                                    ) : (
                                        <a 
                                            href={link.href}
                                            className="text-gray-400 hover:text-higeia-light transition-colors duration-200 text-sm"
                                        >
                                            {link.name}
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 3. Coluna: Links Úteis */}
                    <div>
                        <h3 className="text-white font-semibold text-lg mb-4">Links Úteis</h3>
                        <ul className="space-y-3">
                            {usefulLinks.map((link) => (
                                <li key={link.name}>
                                    <Link 
                                        to={link.href}
                                        onClick={(e) => handleLinkClick(link.href, e)}
                                        className="text-gray-400 hover:text-higeia-light transition-colors duration-200 text-sm"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 4. Coluna: Contato */}
                    <div>
                        <h3 className="text-white font-semibold text-lg mb-4">Contato</h3>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                                <Mail className="h-5 w-5 text-higeia-light flex-shrink-0 mt-0.5" />
                                <a 
                                    href="mailto:contato@higeia.tech"
                                    className="text-gray-400 hover:text-higeia-light transition-colors duration-200 text-sm"
                                >
                                    contato@higeia.tech
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone className="h-5 w-5 text-higeia-light flex-shrink-0 mt-0.5" />
                                <a 
                                    href="tel:+5511999999999"
                                    className="text-gray-400 hover:text-higeia-light transition-colors duration-200 text-sm"
                                >
                                    (53) 3028-3030
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="h-5 w-5 text-higeia-light flex-shrink-0 mt-0.5" />
                                <span className="text-gray-400 text-sm">
                                    Pelotas, RS - Brasil
                                </span>
                            </li>
                        </ul>

                        {/* Redes Sociais */}
                        <div className="mt-6">
                            <h4 className="text-white font-semibold text-sm mb-3">Siga-nos</h4>
                            <div className="flex gap-3">
                                {socialLinks.map((social) => {
                                    const Icon = social.icon;
                                    return (
                                        <a
                                            key={social.name}
                                            href={social.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-higeia-dark text-gray-400 hover:text-white transition-all duration-200"
                                            aria-label={social.name}
                                        >
                                            <Icon className="h-5 w-5" />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divisor */}
                <div className="mt-12 pt-8 border-t border-gray-800">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        
                        {/* Copyright */}
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Lock className="h-4 w-4" />
                            <span>
                                © {currentYear} Higeia.tech. Todos os direitos reservados.
                            </span>
                        </div>

                        {/* Badge de Segurança */}
                        <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-2 text-gray-400">
                                <Shield className="h-4 w-4 text-higeia-light" />
                                <span>100% Seguro</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                                <Heart className="h-4 w-4 text-red-400" />
                                <span>Feito com cuidado</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;

